/**
 * Workspace context bootstrap — the one thing every product shell runs on mount.
 *
 * A direct load or hard refresh of any shell URL (/prioritization/*, /planning/*,
 * /apps/mandate/*) starts with no tenant context: getTenantContext() is null,
 * the user store still says 'unknown', and `workspaceResolutionComplete` is
 * false — so SpaceSwitcher's bootstrapSpaces gate never opens and the sidebar
 * reads "Loading spaces…" for good. This hook resolves the context the way the
 * Priorities layout always has (ensureWorkspaceContext → respect the stored
 * workspace → sync the user → load permissions) and, whatever the outcome,
 * marks resolution complete so everything gated on it proceeds.
 *
 * Until 2026-11-02 the Priorities and Plans layouts each carried their own copy
 * of this effect and the Mandate layout had none — which is why Mandate's
 * embedded pages hung on a cold load. One copy, three shells.
 */

import { useEffect, useRef } from 'react';
import {
  ensureWorkspaceContext,
  updateTenantContextWorkspace,
  type TenantContext,
} from '../lib/tenantContext';
import { setCurrentWorkspaceIdHint } from '../lib/supabaseSync';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { useUserStore } from '../stores/prioritization/userStore';

export interface WorkspaceContextBootstrapOptions {
  /** Runs once with the resolved context (not when resolution returns null). */
  onContext?: (ctx: TenantContext) => void;
  /** Runs once when resolution has finished, whatever its outcome. */
  onSettled?: () => void;
}

/** A display name for the sidebar when the profile has none: the email's local part, spaced. */
export function displayNameFromContext(ctx: TenantContext): string {
  return (
    ctx.userName?.trim() ||
    (ctx.userEmail ? ctx.userEmail.replace(/@.*$/, '').replace(/[._]/g, ' ') : undefined) ||
    ctx.userId
  );
}

export function useWorkspaceContextBootstrap(options: WorkspaceContextBootstrapOptions = {}): void {
  // The effect runs once per mount; callers pass inline callbacks, so hold the
  // latest ones in a ref rather than re-running resolution on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let cancelled = false;
    ensureWorkspaceContext(useTenantStore.getState().currentWorkspaceId)
      .then((ctx) => {
        if (cancelled || !ctx?.workspaceId) return;
        // Respect the user's persisted workspace selection. ensureWorkspaceContext keeps it
        // when the user is a member; otherwise it resolves to the demo or first membership
        // workspace, which is only used when nothing is stored yet.
        const stored = useTenantStore.getState().currentWorkspaceId;
        if (!stored) {
          useTenantStore.getState().setCurrentWorkspace(ctx.workspaceId);
        } else if (stored !== ctx.workspaceId) {
          // A persisted workspace the user is not a member of (a platform admin's choice):
          // keep the selection and sync the module-level context so getCurrentWorkspaceId()
          // returns it immediately.
          updateTenantContextWorkspace(stored);
          setCurrentWorkspaceIdHint(stored);
        }
        // Sync the user from auth so permissions can load (userId was often 'unknown'
        // at first paint) — SpaceSwitcher waits on permissionsLoaded.
        if (ctx.userId) {
          const { setUser, loadPermissions } = useUserStore.getState();
          setUser?.(ctx.userId, displayNameFromContext(ctx), ctx.userEmail);
          loadPermissions?.();
        }
        optionsRef.current.onContext?.(ctx);
      })
      .finally(() => {
        if (cancelled) return;
        optionsRef.current.onSettled?.();
        // Signal that resolution is done regardless of outcome. Without this, people
        // with no workspace membership sit in a loading skeleton forever, and the
        // space switcher never bootstraps.
        useTenantStore.getState().setWorkspaceResolutionComplete();
      });
    return () => {
      cancelled = true;
    };
  }, []);
}
